<template>
<div class="chapter">
<div class="inner_page">
<a id="page_4"></a>
<div class="org">
<h2 class="chtitle">the basics</h2>
</div>
<div class="note">
<div class="img_containerl">
<img alt="images" src="images/note.jpg"/>
</div>
<p class="note">See <span href="#/xhtml/09_Chapter05#page_27" title="Link target outside of Sample">page 27</span> for sources of more detailed information.</p>
</div>
<h3 class="h3o" id="sec1"><span class="bold">essential supplies</span></h3>
<p class="noindent"><span class="green">■</span> <span class="bold">Sewing machine</span> (good working condition, with proper tension [refer to the manufacturer’s guide for proper adjustment], an even stitch, and a good-quality size 80 needle)</p>
<p class="noindent"><span class="green">■</span> 45mm <span class="bold">rotary cutter</span></p>
<div class="img_containerz1">
<img alt="images" src="images/f0004-01.jpg"/>
</div>
<p class="noindent"><span class="green">■</span> 18” × 24” self-healing <span class="bold">rotary cutting mat</span> (must be used with the rotary cutter)</p>
<div class="img_containerz1">
<img alt="images" src="images/f0004-02.jpg"/>
</div>
<p class="noindent"><span class="green">■</span> 6” × 12” <span class="bold">rotary cutting ruler</span></p>
<div class="img_containerz1">
<img alt="images" src="images/f0004-03.jpg"/>
</div>
<p class="noindent"><span class="green">■</span> <span class="bold">Scissors</span> (small, for cutting threads)</p>
<div class="img_containerz1">
<img alt="images" src="images/f0004-04.jpg"/>
</div>
<p class="noindent"><span class="green">■</span> <span class="bold">Pins</span> (thin, fine quilter’s or silk pins work best)</p>
<p class="noindent"><span class="green">■</span> <span class="bold">Sewing thread</span> (good-quality matching or neutral-color cotton)</p>
<div class="img_containerz1">
<img alt="images" src="images/f0004-05.jpg"/>
</div>
<p class="noindent"><span class="green">■</span> <span class="bold">Seam ripper</span> (sharp, good quality)</p>
<div class="img_containerz1">
<img alt="images" src="images/f0004-06.jpg"/>
</div>
<p class="noindent"><span class="green">■</span> <span class="bold">Iron</span></p>
<p class="noindent"><span class="green">■</span> <span class="bold">Safety pins</span> (1” long for basting)</p>
<p class="noindent"><span class="green">■</span> <span class="bold">Perle cotton or crochet cotton thread</span> (for tying and big-stitch hand quilting)</p>
<div class="img_containerz1">
<img alt="images" src="images/f0004-07.jpg"/>
<p class="caption">Presencia perle cotton</p>
</div>
<div class="img_containerz1">
<img alt="images" src="images/f0004-08.jpg"/>
<p class="caption">Crochet cotton</p>
</div>
<p class="noindent"><span class="green">■</span> <span class="bold">Needles</span> (darning or chenille needles with eyes large enough for the perle cotton for tying and big-stitch hand quilting)</p>
<p class="noindent"><span class="green">■</span> <span class="bold">Buttons</span></p>
<div class="img_containerz1">
<img alt="images" src="images/f0004-09.jpg"/>
</div>
<p class="noindent"><span class="greenl">■</span> <span class="bold">Masking tape</span> (narrow width to mark quilting lines)</p>
<h3 class="h3o" id="sec2"><span class="bold">fabric</span></h3>
<p class="noindent">Use only high-quality 100% cotton fabric. Less-expensive cottons can stretch and distort and be very frustrating to work with.</p>
<p class="noindent">Always use a combination of light, medium, and dark fabrics in a variety of print sizes. This will result in interesting, exciting quilts.</p>
<h3 class="h3o" id="sec3"><span class="bold">rotary cutting</span></h3>
<p class="noindent"><span class="green">■</span> Practice and learn to use the rotary cutter safely and properly.</p>
<p class="noindent"><span class="green">■</span> Always close the safety latch on the rotary cutter after each cut.</p>
<p class="noindent"><span class="green">■</span> Always cut away from your body, at a 90° angle.</p>
<a id="page_5"></a>
<p class="noindent"><span class="green">■</span> Hold the rotary cutter as shown, with your index finger extended along the back of the cutter.</p>
<div class="img_containerz1">
<img alt="images" src="images/f0005-01.jpg"/>
<p class="caption">Left-handed</p>
</div>
<div class="img_containerz1">
<img alt="images" src="images/f0005-02.jpg"/>
<p class="caption">Right-handed</p>
</div>
<p class="noindent"><span class="green">■</span> Place the side of the rotary cutter blade directly against the edge of the ruler.</p>
<div class="img_containerz1">
<img alt="images" src="images/f0005-03.jpg"/>
<p class="caption">Place the blade against the edge of the ruler.</p>
</div>
<p class="noindent"><span class="bold"><span class="blue">1.</span></span> Fold the fabric selvage (finished edge) to selvage, then fold again.</p>
<div class="img_containerz1">
<img alt="images" src="images/f0005-04.jpg"/>
<p class="caption">Fold the fabric twice.</p>
</div>
<p class="noindent"><span class="bold"><span class="blue">2.</span></span> Align a vertical line of the ruler with the bottom fold of the fabric. Trim to straighten and square up the raw edges.</p>
<div class="img_containerz1">
<img alt="images" src="images/f0005-05.jpg"/>
<p class="caption">Left-handed</p>
</div>
<div class="img_containerz1">
<img alt="images" src="images/f0005-06.jpg"/>
<p class="caption">Right-handed</p>
</div>
<p class="noindent"><span class="bold"><span class="blue">3.</span></span> Line up the vertical measurement on the ruler with the trimmed edge of the fabric. Cut the size and number of strips indicated in the charts included with the instructions for each project.</p>
<div class="img_containerz1">
<img alt="images" src="images/f0005-07.jpg"/>
<p class="caption">Left-handed</p>
</div>
<div class="img_containerz1">
<img alt="images" src="images/f0005-08.jpg"/>
<p class="caption">Right-handed</p>
</div>
<p class="noindent"><span class="bold"><span class="blue">4.</span></span> Rotate the mat and fabric. Trim off the uneven raw edges to square up the short edges. Line up the measurement on the ruler with the trimmed edge of the fabric. Cut the size and number of units (squares and rectangles) indicated in the charts included with the instructions for each project.</p>
<div class="img_containerz1">
<img alt="images" src="images/f0005-09.jpg"/>
<p class="caption">Left-handed</p>
</div>
<div class="img_containerz1">
<img alt="images" src="images/f0005-10.jpg"/>
<p class="caption">Right-handed</p>
</div>
<p class="noindent"><span class="bold"><span class="blue">5.</span></span> To cut half-square triangles, position the ruler corner to corner and cut the square in half diagonally.</p>
<div class="img_containerz1">
<img alt="images" src="images/f0005-11.jpg"/>
<p class="caption">Left-handed</p>
</div>
<div class="img_containerz1">
<img alt="images" src="images/f0005-12.jpg"/>
<p class="caption">Right-handed</p>
</div>
<a id="page_6"></a>
<h3 class="h3o" id="sec4"><span class="bold">stitching</span></h3>
<p class="noindent">Straight stitching makes nice straight seamlines.</p>
<p class="noindent">Use a stitch length just long enough that a seam ripper will slide nicely under the stitches. Backstitching is not necessary because all the seam ends will be enclosed by other seams.</p>
<h4 class="h4b">¼” SEAM ALLOWANCE</h4>
<p class="noindent">Always use ¼” seam allowances for piecing the quilt tops. This is crucial for accurate results.</p>
<p class="noindent">A special ¼” foot may be available for your machine. It is an invaluable investment for your quiltmaking.</p>
<p class="noindent"><span class="bold"><span class="blue">1.</span></span> Raise the sewing machine needle and presser foot and place your rotary ruler under the needle.</p>
<p class="noindent"><span class="bold"><span class="blue">2.</span></span> Lower the presser foot, then manually ease the needle down on top of the ¼” mark.</p>
<p class="noindent"><span class="bold"><span class="blue">3.</span></span> Place a piece of masking tape along the edge of the ruler to mark ¼” on the throat plate to use as your seam guide.</p>
<div class="img_containerz1">
<img alt="images" src="images/f0006-01.jpg"/>
</div>
<p class="noindent"><span class="bold"><span class="blue">4.</span></span> Sew a sample to check to be sure you are sewing an exact ¼” seam allowance.</p>
<h3 class="h3o" id="sec5"><span class="bold">pinning</span></h3>
<p class="noindent">Pin at least the beginning and end of each seam. More pins make it easier to align the pieces when you sew.</p>
<p class="noindent">When aligning seams that are pressed in opposite directions, place a pin on each side of the seam, no more than ¼” from the seam.</p>
<div class="img_containerz1">
<img alt="images" src="images/f0006-02.jpg"/>
<p class="caption">Place pins.</p>
</div>
<h3 class="h3o" id="sec6"><span class="bold">pressing</span></h3>
<p class="noindent">Press on a firm surface (an ironing board with a single pad).</p>
<p class="noindent">Press the seams in one direction, not open, as follows:</p>
<p class="noindent">Place the pieced unit on the ironing surface with the fabric you want the seam pressed toward on top, open the unit, and press. The seam should be pressed toward the correct fabric.</p>
<h3 class="h3o" id="sec7"><span class="bold">finishing the quilt</span></h3>
<h4 class="h4ab">BATTING AND BACKING</h4>
<p class="noindent"><span class="bold">Batting</span> (low-loft polyester, approximately 3” larger than the quilt top on each side)</p>
<p class="noindent"><span class="bold">Backing</span> (approximately 3” larger than the quilt top on each side)</p>
<p class="noindent">If your quilt top is larger than one width of fabric, you will need to sew pieces of fabric together to make the backing (trim off the selvages first). If you’re using leftover fabrics, sew pieces together to achieve the size listed in the Materials chart. You may have some leftover fabrics from making the project quilts to use for backing and binding.</p>
<h4 class="h4b">LAYERING</h4>
<p class="noindent"><span class="bold"><span class="blue">1.</span></span> Place the backing wrong side up. Secure the backing to a large, flat surface, pulling the backing smooth and taut (not too tight). Use masking tape to secure on a table or hard floor or T-pins on nonloop carpet.</p>
<p class="noindent"><span class="bold"><span class="blue">2.</span></span> Place the batting on top of the backing and smooth out the wrinkles.</p>
<p class="noindent"><span class="bold"><span class="blue">3.</span></span> Smooth the quilt top onto the batting, right side up.</p>
<h4 class="h4b">BASTING</h4>
<p class="noindent">Pin baste evenly across the quilt about every 3” with safety pins.</p>
<div class="note">
<div class="img_containerl">
<img alt="images" src="images/note.jpg"/>
</div>
<p class="note">If you plan your tying or quilting design before you pin baste, you can place the safety pins so they will not be in the way.</p>
</div>
<a id="page_7"></a>
<h4 class="h4b">SECURING THE LAYERS</h4>
<h5 class="h5"><span class="bold"><em class="italic">Securing Method #1: Tying</em></span></h5>
<p class="noindent"><span class="bold"><span class="blue">1.</span></span> Decide whether you want the knots and tails to be on the front or the back of the quilt.</p>
<p class="noindent"><span class="bold"><span class="blue">2.</span></span> Thread a large-eyed needle with the tying thread. (The quilts in this book were tied with DMC perle cotton or crochet cotton thread.)</p>
<div class="note">
<div class="img_containerl">
<img alt="images" src="images/note.jpg"/>
</div>
<p class="note">You can use a single or double thread, depending on how much you want the thread to show.</p>
</div>
<p class="noindent"><span class="bold"><span class="blue">3.</span></span> Push the needle through all 3 layers, so it comes out through the other side.</p>
<div class="note">
<div class="img_containerl">
<img alt="images" src="images/note.jpg"/>
</div>
<p class="note">If you want the knots on the front, push the needle in from the front. If you want the knots on the back, push the needle in from the back.</p>
</div>
<p class="noindent"><span class="bold"><span class="blue">4.</span></span> Push the needle back through the layers, approximately ⅛”–¼” from where it originally went through the layers. Pull the thread, leaving a 1½”–2” tail.</p>
<p class="noindent"><span class="bold"><span class="blue">5.</span></span> Tie the knot as shown below. This knot is more secure than a square knot. Trim the thread ends to the length you want.</p>
<div class="img_containerz1">
<img alt="images" src="images/f0007-01.jpg"/>
<p class="caption">A. Tie a half-knot.</p>
</div>
<div class="img_containerz1">
<img alt="images" src="images/f0007-02.jpg"/>
<p class="caption">B. Pull it tight.</p>
</div>
<div class="img_containerz1">
<img alt="images" src="images/f0007-03.jpg"/>
<p class="caption">Left-handed</p>
<img alt="images" src="images/f0007-04.jpg"/>
<p class="caption">Right-handed</p>
<p class="caption">C. Hold the two strands and the needle as shown.</p>
</div>
<div class="img_containerz1">
<img alt="images" src="images/f0007-05.jpg"/>
<p class="caption">Left-handed</p>
<img alt="images" src="images/f0007-06.jpg"/>
<p class="caption">Right-handed</p>
<p class="caption">D. Take the needle around the threads. Then, pull the needle through the loops.</p>
</div>
<div class="img_containerz1">
<img alt="images" src="images/f0007-07.jpg"/>
<p class="caption">E. Let go of the thread end that is attached to the needle and hold only the short thread tail in your other hand.</p>
</div>
<div class="img_containerz1">
<img alt="images" src="images/f0007-08.jpg"/>
<p class="caption">F. Pull the knot tight</p>
</div>
<div class="note">
<div class="img_containerl">
<img alt="images" src="images/note.jpg"/>
</div>
<p class="note">You can tie on buttons using the same method.</p>
</div>
<a id="page_8"></a>
<h5 class="h5"><span class="bold"><em class="italic">Securing Method #2: Big-Stitch Hand Quilting</em></span></h5>
<div class="note">
<div class="img_containerl">
<img alt="images" src="images/note.jpg"/>
</div>
<p class="note">It is not necessary to use a quilting hoop or frame when big-stitch hand quilting, but you have to be very careful not to pull the stitches too tight. You want your quilt to remain very flat and smooth.</p>
<p class="note">The methods shown here for bigstitch hand quilting differ from traditional hand quilting methods because of the thickness of the thread and the size of the stitches.</p>
</div>
<p class="noindent1"><span class="bold"><span class="blue">1.</span></span> Knot one end of the thread. (The quilts in this book were tied with either DMC perle cotton or crochet cotton thread.)</p>
<div class="note">
<div class="img_containerl">
<img alt="images" src="images/note.jpg"/>
</div>
<p class="note">Whenever possible, start and end the line of stitching at the edge of the quilt. This will hide the knots. If you have to start or stop the stitches in the middle of the quilt, make your knots as small as possible on the back of the quilt.</p>
</div>
<p class="noindent1"><span class="bold"><span class="blue">2.</span></span> Insert the needle from the back of the quilt and pull the thread through to the front. Sew a running stitch, making the stitches approximately ¼” long.</p>
<div class="img_containerz1">
<img alt="images" src="images/f0008-01.jpg"/>
<p class="caption">Running stitch</p>
</div>
<p class="noindent"><span class="bold"><span class="blue">3.</span></span> When you come to the end of the stitching or have approximately 6” of thread left, knot the thread on the back of the quilt as shown below.</p>
<div class="img_containerz1">
<img alt="images" src="images/f0008-02.jpg"/>
<p class="caption">Left-handed</p>
<img alt="images" src="images/f0008-03.jpg"/>
<p class="caption">Right-handed</p>
<p class="caption">A. Take a tiny stitch through just the backing and batting.</p>
</div>
<div class="img_containerz1">
<img alt="images" src="images/f0008-04.jpg"/>
<p class="caption">Left-handed</p>
<img alt="images" src="images/f0008-05.jpg"/>
<p class="caption">Right-handed</p>
<p class="caption">B. Put the needle through the loop.</p>
</div>
<div class="img_containerz1">
<img alt="images" src="images/f0008-06.jpg"/>
<p class="caption">C. Pull it tight.</p>
</div>
<div class="img_containerz1">
<img alt="images" src="images/f0008-07.jpg"/>
<p class="caption">Left-handed</p>
<img alt="images" src="images/f0008-08.jpg"/>
<p class="caption">Right-handed</p>
<p class="caption">D. Run the needle through the backing and batting about ½”.</p>
</div>
<div class="img_containerz1">
<img alt="images" src="images/f0008-09.jpg"/>
<p class="caption">Left-handed</p>
<img alt="images" src="images/f0008-10.jpg"/>
<p class="caption">Right-handed</p>
<p class="caption">E. Trim off the thread where it exits the batting.</p>
</div>
<a id="page_9"></a>
<h5 class="h5"><span class="bold"><em class="italic">Securing Method #3: Machine Quilting in the Ditch</em></span></h5>
<p class="noindent">This is a simple method of machine quilting in the ditch to get you started. Machine quilting takes practice.</p>
<div class="note">
<div class="img_containerl">
<img alt="images" src="images/note.jpg"/>
</div>
<p class="note">The larger the quilt, the more challenging it is to machine quilt.</p>
</div>
<p class="noindent"><span class="green">■</span> You must use a walking/even-feed foot on your sewing machine for the layers to feed through the machine evenly.</p>
<p class="noindent"><span class="green">■</span> Refer to the sewing machine manufacturer’s instructions for thread tension guidance. Sew on a test piece of layered fabric, batting, and backing until you achieve the perfect thread tension.</p>
<p class="noindent"><span class="green">■</span> Begin and end the lines of stitching using very tiny stitches.</p>
<p class="noindent"><span class="green">■</span> Use a slightly longer stitch than you use for piecing.</p>
<p class="noindent"><span class="green">■</span> Machine quilt long lines of stitches from one edge of the quilt to the other whenever possible, starting from the center and working out. Then, quilt the shorter lines to finish quilting around the blocks.</p>
<p class="noindent"><span class="green">■</span> Stitch as close to the “ditch” as you can without stitching the adjoining fabric.</p>
<div class="img_containerz1">
<img alt="images" src="images/f0009-01.jpg"/>
<p class="caption">Stitch in the ditch.</p>
</div>
<h4 class="h4b">BINDING</h4>
<p class="noindent">We recommend that you use leftover fabrics for binding and supplement as necessary. You can use 1 to 4 different fabrics. Look at the quilts throughout the book for ideas.</p>
<h5 class="h5"><span class="bold"><em class="italic">Binding Method #1: Squared Corners, Hand Finished on the Back</em></span></h5>
<div class="img_containerz1">
<img alt="images" src="images/f0009-02.jpg"/>
</div>
<p class="noindent"><span class="bold"><span class="blue">1.</span></span> Trim the batting and backing even with the edges of the quilt top.</p>
<p class="noindent"><span class="bold"><span class="blue">2.</span></span> Cut as many 2¼”-wide strips as you need to go all the way around the quilt, plus 10” or more extra.</p>
<p class="noindent"><span class="bold"><span class="blue">3.</span></span> Sew the strips together using diagonal seams to make 4 lengths at least 2” longer than the edges of the quilt.</p>
<div class="img_containerz1">
<img alt="images" src="images/f0009-03.jpg"/>
<p class="caption">Piece the strips using diagonal seams. Trim.</p>
</div>
<div class="img_containerz1">
<img alt="images" src="images/f0009-04.jpg"/>
<p class="caption">Press.</p>
</div>
<p class="noindent"><span class="bold"><span class="blue">4.</span></span> Measure the quilt width through the middle from side to side. Trim 2 binding strips the width of the quilt plus 1”.</p>
<p class="noindent"><span class="bold"><span class="blue">5.</span></span> Fold the strips in half lengthwise, wrong sides together, and press.</p>
<div class="img_containerz1">
<img alt="images" src="images/f0009-05.jpg"/>
<p class="caption">Fold and press.</p>
</div>
<p class="noindent"><span class="bold"><span class="blue">6.</span></span> Align the raw edges of the binding along the top edge of the front of the quilt. Let the binding extend ½” past the corners of the quilt. Sew using a ¼” seam allowance. Repeat for the bottom edge of the quilt.</p>
<div class="img_containerz1">
<img alt="images" src="images/f0009-06.jpg"/>
<p class="caption">Sew the binding to the top and bottom edges of the quilt.</p>
</div>
<p class="noindent"><span class="bold"><span class="blue">7.</span></span> Flip the finished edge of the binding over the raw edge of the quilt and hand slipstitch the binding to the back of the quilt. Trim the ends even with the corners of the quilt.</p>
<div class="img_containerz1">
<img alt="images" src="images/f0009-07.jpg"/>
<p class="caption">Fold the binding to the back, stitch, and trim.</p>
</div>
<a id="page_10"></a>
<p class="noindent"><span class="bold"><span class="blue">8.</span></span> Measure the quilt length through the middle from top to bottom. Trim 2 binding strips the length of the quilt plus 1”. Fold and press. Align and sew the strips as before, leaving ½” of the binding past the corners. Fold over the ends of the binding to create a finished edge before folding the binding to the back of the quilt. Hand slipstitch the binding, including the ends, in place.</p>
<div class="img_containerz1">
<img alt="images" src="images/f0010-01.jpg"/>
<p class="caption">Fold the ends and stitch the side binding.</p>
</div>
<h5 class="h5"><span class="bold"><em class="italic">Binding Method #2: Squared Corners, Machine Finished on the Front</em></span></h5>
<div class="img_containerz1">
<img alt="images" src="images/f0010-02.jpg"/>
</div>
<p class="noindent"><span class="bold"><span class="blue">1.</span></span> Follow Steps 1–5 for Binding Method #1, <a href="#/xhtml/05_Chapter01#page_9">page 9</a>.</p>
<p class="noindent"><span class="bold"><span class="blue">2.</span></span> Align the raw edges of the binding with the top edge of the back of the quilt. Let the binding extend ½” past the corners of the quilt. Sew using a ¼” seam allowance. Repeat for the bottom edge of the quilt.</p>
<div class="img_containerz1">
<img alt="images" src="images/f0010-03.jpg"/>
<p class="caption">Sew the binding to the top edge of the quilt.</p>
</div>
<p class="noindent"><span class="bold"><span class="blue">3.</span></span> Flip the finished edge of the binding over the raw edge of the quilt and machine stitch to the front of the quilt using straight or decorative stitches. Trim the ends even with the edge of the quilt.</p>
<div class="img_containerz1">
<img alt="images" src="images/f0010-04.jpg"/>
<p class="caption">Fold the binding to the front, stitch, and trim.</p>
</div>
<p class="noindent"><span class="bold"><span class="blue">4.</span></span> Measure the quilt length through the middle from top to bottom. Trim 2 binding strips the length of the quilt plus 1”. Fold and press. Align and sew the strips to the back of the quilt as in Step 2, leaving ½” of binding past the corners. Fold over the ends of the binding to create a finished edge before folding the binding to the front of the quilt. Machine stitch to the front of the quilt, as in Step 3.</p>
<div class="img_containerz1">
<img alt="images" src="images/f0010-05.jpg"/>
<p class="caption">Fold the ends and stitch the side binding.</p>
</div>
<h5 class="h5"><span class="bold"><em class="italic">Binding Method #3: Mitered Corners, Hand Finished on the Back</em></span></h5>
<div class="img_containerz1">
<img alt="images" src="images/f0010-06.jpg"/>
</div>
<p class="noindent"><span class="bold"><span class="blue">1.</span></span> Trim the batting and backing even with the edges of the quilt top.</p>
<p class="noindent"><span class="bold"><span class="blue">2.</span></span> Cut as many 2¼”-wide strips as you need to go all the way around the quilt, plus 10” or more extra.</p>
<p class="noindent"><span class="bold"><span class="blue">3.</span></span> Sew the strips together using diagonal seams to make one long length.</p>
<div class="img_containerz1">
<img alt="images" src="images/f0010-07.jpg"/>
<p class="caption">Piece the strips using diagonal seams. Trim.</p>
</div>
<div class="img_containerz1">
<img alt="images" src="images/f0010-08.jpg"/>
<p class="caption">Press.</p>
</div>
<p class="noindent"><span class="bold"><span class="blue">4.</span></span> Fold the strips in half lengthwise, wrong sides together, and press.</p>
<div class="img_containerz1">
<img alt="images" src="images/f0010-09.jpg"/>
<p class="caption">Fold and press.</p>
</div>
<a id="page_11"></a>
<p class="noindent"><span class="bold"><span class="blue">5.</span></span> With the raw edges of the quilt and binding aligned, pin the binding to the front of the quilt, beginning a few inches from a corner, leaving the first 6” of the binding unattached. Start sewing using a ¼” seam allowance.</p>
<p class="noindent"><span class="bold"><span class="blue">6.</span></span> Stop ¼” from the first corner of the quilt and backstitch one stitch.</p>
<div class="img_containerz1">
<img alt="images" src="images/f0011-01.jpg"/>
<p class="caption">Stitch ¼” from a corner.</p>
</div>
<p class="noindent"><span class="bold"><span class="blue">7.</span></span> Lift the presser foot and needle. Rotate the quilt one-quarter turn. Fold the binding at a right angle so it extends straight above the quilt.</p>
<div class="img_containerz1">
<img alt="images" src="images/f0011-02.jpg"/>
<p class="caption">Fold the binding up.</p>
</div>
<p class="noindent"><span class="bold"><span class="blue">8.</span></span> Bring the binding down, even with the edge of the quilt. Begin sewing again at the folded edge, stopping ¼” from the next corner and backstitching one stitch.</p>
<div class="img_containerz1">
<img alt="images" src="images/f0011-03.jpg"/>
<p class="caption">Fold down and stitch.</p>
</div>
<p class="noindent"><span class="bold"><span class="blue">9.</span></span> Repeat for all the sides of the quilt. Stop sewing 6” from where you started.</p>
<p class="noindent"><span class="bold"><span class="blue">10.</span></span> Overlap the tails and trim, leaving a 2” overlap.</p>
<p class="noindent"><span class="bold"><span class="blue">11.</span></span> Turn the beginning tail end under ¼”.</p>
<div class="img_containerz1">
<img alt="images" src="images/f0011-04.jpg"/>
<p class="caption">Turn the end under ¼”.</p>
</div>
<p class="noindent"><span class="bold"><span class="blue">12.</span></span> Place the ending tail end inside the beginning tail end.</p>
<div class="img_containerz1">
<img alt="images" src="images/f0011-05.jpg"/>
<p class="caption">Place the ending tail inside the beginning tail.</p>
</div>
<p class="noindent"><span class="bold"><span class="blue">13.</span></span> Adjust to the proper length, pin, and sew to finish the seam.</p>
<div class="img_containerz1">
<img alt="images" src="images/f0011-06.jpg"/>
<p class="caption">Pin, then sew.</p>
</div>
<p class="noindent"><span class="bold"><span class="blue">14.</span></span> Fold to the back of the quilt and hand stitch to finish.</p>
<div class="img_containerz1">
<img alt="images" src="images/f0011-07.jpg"/>
<p class="caption">Fold to the back and stitch.</p>
</div>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// 05_Chapter01.vue?4327e634